import React, { useState } from "react";
import {
  FormControl,
  FormHelperText,
  Typography,
  InputAdornment,
  IconButton,
  Box,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import { Eye, EyeOff } from "lucide-react";

/**
 * Reusable input using MUI OutlinedInput.
 * Label stays above as simple text (no floating).
 * When type="password", shows an eye icon to toggle visibility.
 */
const TextInput = ({
  name,
  label,
  type = "text",
  value = "",
  onChange,
  placeholder = "Please enter text",
  required = false,
  error = "",
  disabled = false,
  multiline = false,
  rows = 1,
  id,
  ...rest
}) => {
  const inputId = id || (name ? `input-${name}` : undefined);
  const isPassword = type === "password";
  const [showPassword, setShowPassword] = useState(false);
  const inputType = isPassword ? (showPassword ? "text" : "password") : type;

  return (
    <FormControl
      fullWidth
      error={Boolean(error)}
      disabled={disabled}
      required={required}
    >
      {label && (
        <Typography
          component="label"
          htmlFor={inputId}
          variant="body2"
          fontWeight={600}
          sx={{
            display: "block",
            mb: 0.5,
            color: error ? "error.main" : "text.secondary",
            transition: "color 0.2s ease-in-out",
            fontSize: { xs: "0.875rem", "2xl": "0.9rem", "3xl": "1rem", "4xl": "1.3rem" },
            mb: { xs: 0.5, "2xl": 1, "4xl": 2 },
          }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
              *
            </Box>
          )}
        </Typography>
      )}
      <OutlinedInput
        id={inputId}
        name={name}
        type={inputType}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        multiline={multiline}
        rows={rows}
        error={Boolean(error)}
        sx={{
          height: multiline ? "auto" : { xs: 40, "2xl": 48, "3xl": 60, "4xl": 80 },
          fontSize: { xs: "0.875rem", "2xl": "1rem", "3xl": "1.25rem", "4xl": "1.75rem" },
          "& .MuiOutlinedInput-input": {
            px: { xs: 1.5, "2xl": 2, "4xl": 4 },
            py: multiline ? 1.5 : 0,
            height: multiline ? "auto" : { xs: 40, "2xl": 48, "3xl": 60, "4xl": 80 },
            display: multiline ? "block" : "flex",
            alignItems: multiline ? "initial" : "center",
          },
          "&.Mui-error fieldset": {
            borderColor: "error.main !important",
          },
        }}
        endAdornment={
          isPassword ? (
            <InputAdornment position="end">
              <IconButton
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
                aria-label={showPassword ? "Hide password" : "Show password"}
                size="small"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </IconButton>
            </InputAdornment>
          ) : null
        }
        {...rest}
      />
      {error && (
        <FormHelperText sx={{ ml: 0, fontSize: { xs: "0.75rem", "2xl": "0.875rem", "4xl": "1.25rem" } }}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default TextInput;
